import React, {useState, useEffect, useCallback, useLayoutEffect} from 'react';
import {
  View,
  Text,
  Platform,
  ScrollView,
  Image,
  ImageBackground,
  Dimensions,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
} from 'react-native';
import {makeStyles, Overlay} from 'react-native-elements'; // rne-netzon replaced
import {colorPalette} from '../../../core/config/color.config';

import {useFocusEffect, useNavigation} from '@react-navigation/native';

import {RNEText, SVGIcon, RNEButton, IconName} from '../../components';
import {HeaderIconRight} from '../../views';

import {formatDateString} from '../../utils/date.utils';
import {NavigationKey} from '../../navigations/config';
import ResendVerificationSuccessModal from '../../views/modal/ResendVerificationSuccessModal';
import DeniedResendVerificationModal from '../../views/modal/DeniedResendVerificationModal';
import ShareDoctorProfileModal from '../../views/modal/ShareDoctorProfileModal';
import {useToast} from 'react-native-fast-toast';
import {
  useGetProfessionalProfile,
  useRequestVerification,
  useGetPractitionerShareLink,
} from '../../server/react-query/usePractitioner';
import {PractitionerStatus} from '../../server/types/practitioner-status.types';
import {formatAmount, getConsultationFeeWithService} from '../../utils/currency.utils';
import {width, height, totalSize} from 'react-native-dimension';
import {usePullDownRefreshContext} from '../../stores/pullDownRefreshStore';
import DashboardBackgroundImg from '../../../assets/images/Ellipse_350.png';

import Ionicons from 'react-native-vector-icons/Ionicons';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import FontAwesome5 from 'react-native-vector-icons/FontAwesome5';

const DoctorViewProfessionalProfileScreen = () => {
  const navigation = useNavigation();

  const {status, data, error, isFetching, refetch} =
    useGetProfessionalProfile();

  const [showDeniedRequestModal, setShowDeniedRequestModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  const [statusIcon, setStatusIcon] = useState<IconName>('profile-complete');
  const [iconSize, setIconSize] = useState<number>(
    Platform.OS === 'web' ? 36 : 24,
  );

  const [overrideCardsOverlay, setOverrideCardsOverlay] = useState({
    bottom: 15,
  });
  const [overrideFormContainer, setOverrideFormContainer] = useState({
    marginVertical: -10,
  });

  const goToEditProfessionalProfile = () => {
    setShowDeniedRequestModal(false);

    navigation.navigate(NavigationKey.DOCTOR_EDIT_PROFESSIONAL_PROFILE);
  };

  const goToDashboard = () => {
    setShowSuccessModal(false);
    navigation.navigate(NavigationKey.DOCTOR_SCREEN_LANDING);
  };

  const loadingPropsStyle = {
    color: colorPalette.black,
  };

  const toast = useToast();
  const verificationMutation = useRequestVerification();
  const shareLinkMutation = useGetPractitionerShareLink();

  const onSharePress = async () => {
    try {
      const {url} = await shareLinkMutation.mutateAsync();
      setShareUrl(url);
      setShowShareModal(true);
    } catch (err) {
      console.log('share profile link error', err);
      toast?.show('Unable to generate your share link. Please try again.', {
        type: 'danger',
      });
    }
  };

  useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <HeaderIconRight iconName="share-white" onClick={onSharePress} />
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation]);
  const requestVerification = async () => {
    try {
      if (data?.qualification == null) {
        setShowDeniedRequestModal(true);
      } else {
        await verificationMutation.mutateAsync();
        setShowSuccessModal(true);
      }
    } catch (err) {
      console.log('err', err);
      if (err?.data?.error) {
        toast?.show(err?.data?.error, {type: 'danger'});
      }
    }
  };

  const onTextLayout = useCallback(e => {
    if (e.nativeEvent.lines.length >= 2) {
      setOverrideCardsOverlay({
        bottom: 10,
      });
      setOverrideFormContainer({
        marginVertical: -5,
      });
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch]),
  );

  useEffect(() => {
    switch (data?.status) {
      case PractitionerStatus.VERIFIED:
        setStatusIcon('profile-complete');
        setIconSize(Platform.OS === 'web' ? 36 : 24);
        break;

      case PractitionerStatus.UNVERIFIED:
        setStatusIcon('close-gray-70');
        setIconSize(24);
        break;

      case PractitionerStatus.DECLINED:
        setStatusIcon('request-denied');
        setIconSize(Platform.OS === 'web' ? 16 : 12);
        break;

      case PractitionerStatus.PENDING:
        setStatusIcon('request-cancelled');
        setIconSize(Platform.OS === 'web' ? 16 : 12);
        break;

      default:
        break;
    }
  }, [data?.status]);

  const {setCanRefresh} = usePullDownRefreshContext();

  const [scrollViewHeight, setScrollViewHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useFocusEffect(
    useCallback(() => {
      if (scrollViewHeight > 0 && contentHeight > 0) {
        const isScrollable = contentHeight > scrollViewHeight;

        if (!isScrollable) {
          setCanRefresh(true); // always allow pull-down if not scrollable
        } else {
          setCanRefresh(scrollY <= 0); // only allow when scrolled to top
        }
      }
    }, [scrollViewHeight, contentHeight, scrollY, setCanRefresh]),
  );
  const handleScroll = (e: any) => {
    const y = e.nativeEvent.contentOffset.y;
    setScrollY(y);

    const isScrollable = contentHeight > scrollViewHeight;

    if (!isScrollable) {
      setCanRefresh(true); // still always true if no scroll
    } else {
      setCanRefresh(y <= 0); // only true when back at the very top
    }
  };

  return (
    <>
    <ScrollView
      style={{backgroundColor: 'white'}}
      onScroll={handleScroll}
      onLayout={e => {
        setScrollViewHeight(e.nativeEvent.layout.height);
      }}
      onContentSizeChange={(contentWidth, contentHeight) => {
        setContentHeight(contentHeight);
      }}>
      <View
        style={{
          backgroundColor: colorPalette.purple,
          borderBottomLeftRadius: totalSize(5),
          borderBottomRightRadius: totalSize(5),
          alignContent: 'center',
          alignItems: 'center',
          paddingBottom: totalSize(10),
        }}>
        <View style={mobileStyles.photoContainer}>
          {data?.photo?.url ? (
            <ImageBackground
              source={{
                uri: data?.photo.url,
              }}
              style={mobileStyles.photoStyle}
              imageStyle={mobileStyles.photoContainerStyle}
            />
          ) : (
            <View
              style={[
                mobileStyles.photoPlaceholder,
                mobileStyles.photoPlaceholderMobile,
              ]}>
              <SVGIcon name="user-white" size={56} />
            </View>
          )}
          <View style={mobileStyles.statusIndicator}>
            <SVGIcon name={statusIcon} size={iconSize} />
          </View>
        </View>

        <View style={mobileStyles.nameContainer}>
          <RNEText
            label={`Dr. ${data?.humanName.given[0] || ''} ${data?.humanName?.family || ''}`}
            variant="header"
            color="white"
            weight="bold"
            align="center"
            numberOfLines={2}
            ellipsizeMode="tail"
            onTextLayout={onTextLayout}
          />
        </View>

        <View style={mobileStyles.dateContainer}>
          <RNEText
            label={
              data?.practitionerRole[0]
                ? data?.practitionerRole[0].specialty.text
                : '[Specialization]'
            }
            variant="body"
            color="white"
            align="center"
          />
        </View>

        <View style={mobileStyles.actionWrapper}>
          <View style={mobileStyles.buttonContainer}>
            <RNEButton
              title="Edit Profile"
              color="white"
              variant="outlined"
              onPress={goToEditProfessionalProfile}
            />
          </View>

          {data?.status !== PractitionerStatus.VERIFIED && (
            <View
              style={[
                mobileStyles.buttonContainer,
                mobileStyles.rightContainer,
              ]}>
              <RNEButton
                title="Request Verification"
                color="secondary"
                onPress={requestVerification}
                loading={verificationMutation.isLoading}
              />
            </View>
          )}
        </View>
      </View>

      <View style={[mobileStyles.cardsOverlay]}>
        <View style={mobileStyles.cardsContainer}>
          <View style={mobileStyles.cardContainer}>
            <View style={mobileStyles.cardIcon}>
              <SVGIcon name="map" size={28} />
            </View>
            <RNEText
              label={
                data?.organization[0] &&
                data?.organization[0]?.address &&
                data?.organization[0]?.address.city
                  ? data?.organization[0]?.address?.city
                  : '[City]'
              }
              variant="button"
              color="black"
              align="center"
              weight="bold"
            />
          </View>
          <View
            style={[mobileStyles.cardContainer, mobileStyles.rightContainer]}>
            <View style={mobileStyles.cardIcon}>
              <SVGIcon name="price" size={28} />
            </View>
            <RNEText
              label={
                data?.fee
                  ? `${data.fee.amount.currency} ${formatAmount(
                      getConsultationFeeWithService(data?.fee.amount.value),
                    )}`
                  : '[No Data]'
              }
              variant="button"
              color="black"
              align="center"
              weight="bold"
            />
          </View>
        </View>
        <RNEText
          label="A ₱200 service fee is automatically added to this consultation fee."
          variant="body"
          color="grey"
          align="center"
          style={{marginTop: height(1)}}
        />
      </View>

      <View style={[mobileStyles.formContainer]}>
        <View style={mobileStyles.sectionContainer}>
          <View style={mobileStyles.infoContainer}>
            <View style={mobileStyles.iconContainer}>
              <SVGIcon name="general-practitioner" size={28} />
            </View>
            <RNEText
              label={
                data?.practitionerRole[0]
                  ? data?.practitionerRole[0].specialty.text
                  : '[Specialization]'
              }
              variant="body"
              color="black"
            />
          </View>

          <View style={mobileStyles.infoContainer}>
            <View style={mobileStyles.iconContainer}>
              <SVGIcon name="allied-health-professionals" size={28} />
            </View>
            <RNEText
              label={
                data?.qualification
                  ? data?.qualification.identifier
                  : '[License No.]'
              }
              variant="body"
              color="black"
            />
          </View>

          <View style={mobileStyles.infoContainer}>
            <View style={mobileStyles.iconContainer}>
              <SVGIcon name="calendar" size={28} />
            </View>
            <RNEText
              label={
                data?.birthdate
                  ? formatDateString(data?.birthdate).toString()
                  : '[Birthdate]'
              }
              variant="body"
              color="black"
            />
          </View>
        </View>

        <View style={mobileStyles.formTitleContainer}>
          <RNEText
            label="Cases I Usually Handle:"
            variant="body"
            color="accent"
            weight="bold"
          />
        </View>

        <View style={mobileStyles.sectionContainer}>
          <View>
            <RNEText
              label={data?.handledCases ? data?.handledCases : '[No Data]'}
              variant="body"
              color="black"
            />
          </View>
        </View>

        <View
          style={[mobileStyles.formTitleContainer, {marginTop: height(2.5)}]}>
          <RNEText
            label="Bank Details"
            variant="title"
            color="accent"
            weight="bold"
          />
        </View>

        <View style={mobileStyles.sectionContainer}>
          <View style={mobileStyles.infoContainer}>
            <View style={mobileStyles.iconContainer}>
              <SVGIcon name="home" size={28} />
            </View>
            <RNEText
              label={data?.bankName ? data?.bankName : '[Bank Name]'}
              variant="body"
              color="black"
            />
          </View>

          <View style={mobileStyles.infoContainer}>
            <View style={mobileStyles.iconContainer}>
              <SVGIcon name="pay" size={28} />
            </View>
            <RNEText
              label={data?.bankAccount ? data?.bankAccount : '[Account Number]'}
              variant="body"
              color="black"
            />
          </View>
        </View>
      </View>
    </ScrollView>

    <Overlay
        isVisible={isFetching}
        backdropStyle={{backgroundColor: 'rgba(0,0,0,0.4)'}}
        overlayStyle={{
          backgroundColor: 'transparent',
          elevation: 0,
          shadowOpacity: 0,
        }}>
        <ActivityIndicator size="large" color={colorPalette.secondary} />
      </Overlay>
      <Overlay
        isVisible={showDeniedRequestModal}
        overlayStyle={
          Platform.OS === 'web'
            ? webStyles.overlayContainerWeb
            : mobileStyles.overlayContainerMobile
        }>
        <DeniedResendVerificationModal
          onCancel={() => setShowDeniedRequestModal(false)}
          onConfirm={goToEditProfessionalProfile}
        />
      </Overlay>
      <Overlay
        isVisible={showSuccessModal}
        overlayStyle={
          Platform.OS === 'web'
            ? webStyles.overlayContainerWeb
            : mobileStyles.overlayContainerMobile
        }>
        <ResendVerificationSuccessModal onConfirm={goToDashboard} />
      </Overlay>
      <Overlay
        isVisible={showShareModal}
        onBackdropPress={() => setShowShareModal(false)}
        overlayStyle={
          Platform.OS === 'web'
            ? webStyles.shareOverlayWeb
            : mobileStyles.shareOverlayMobile
        }>
        {!!shareUrl && (
          <ShareDoctorProfileModal
            shareUrl={shareUrl}
            doctorName={`Dr. ${data?.humanName?.given?.[0] || ''} ${data?.humanName?.family || ''}`.trim()}
            onClose={() => setShowShareModal(false)}
          />
        )}
      </Overlay>
      </>
  );
};

export default DoctorViewProfessionalProfileScreen;

const webStyles = StyleSheet.create({
  mainContainer: {
    flex: 1,
    height: '100%',
    width: '100%',
    backgroundColor: '#F6F7FB',
  },
  topBackground: {
    backgroundColor: colorPalette.purple,
    borderBottomRightRadius: 50,
    height: 420,
  },
  titleContainer: {
    flexDirection: 'row',
    marginHorizontal: 100,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: colorPalette.white,
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 100,
    paddingRight: 100,
    paddingVertical: 60,
  },
  userIcon: {
    position: 'relative',
    backgroundColor: 'transparent',
    marginRight: 48,
    borderRadius: 100,
    height: 140,
    width: 140,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colorPalette.white,
  },
  imageWrapper: {
    width: '100%',
    height: '100%',
    borderRadius: 100,
  },
  statusIndicator: {
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    bottom: 4,
    right: 4,
    backgroundColor: colorPalette.white,
    width: 24,
    height: 24,
    borderRadius: 24,
  },
  rightHeaderGroup: {
    flex: 1,
  },
  dateActionsGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  dateContainer: {
    flex: 1,
    height: 48,
    minWidth: 250,
    justifyContent: 'center',
  },
  formContainer: {
    backgroundColor: colorPalette.white,
    borderRadius: 20,
    marginHorizontal: 100,
    top: -100,
    paddingTop: 64,
    paddingBottom: 40,
    paddingHorizontal: 60,
  },
  formTitleContainer: {
    paddingLeft: 24,
    flexDirection: 'row',
  },
  labelGroupContainer: {
    width: '100%',
    marginVertical: 40,
  },
  rowGroupContainer: {
    flexDirection: 'row',
    marginBottom: 40,
  },
  infoContainer: {
    paddingLeft: 24,
    marginBottom: 4,
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  iconContainer: {
    width: 60,
  },
  actionWrapper: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  buttonContainer: {
    marginLeft: 24,
  },
  buttonStyle: {
    width: 180,
  },
  overlayContainerWeb: {
    borderRadius: 16,
    paddingHorizontal: 48,
    paddingVertical: 24,
  },
  shareOverlayWeb: {
    borderRadius: 16,
    padding: 0,
  },
});

const mobileStyles = StyleSheet.create({
  content: {
    flex: 1,
    width: '100%',
  },
  header: {
    // backgroundColor: colorPalette.purple,
    // height: height(55),
    // borderBottomStartRadius: 235,
    // borderBottomEndRadius: 235,
    // marginHorizontal: -35,
    // position: 'relative',
    // alignItems: 'center',
  },
  photoContainer: {
    position: 'relative',
    width: 112,
    height: 112,
    borderRadius: 56,
    marginTop: 32,
    marginBottom: 16,
  },
  photoContainerStyle: {
    borderRadius: 56,
    shadowColor: '#050B5A',
    shadowOffset: {
      width: 5,
      height: 20,
    },
    shadowRadius: 20,
    // elevation: 10,
    // zIndex: 10,
  },
  photoStyle: {
    flex: 1,
    justifyContent: 'center',
    resizeMode: 'cover',
  },
  photoPlaceholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  photoPlaceholderMobile: {
    borderWidth: 1,
    borderColor: colorPalette.white,
    borderRadius: 56,
  },
  statusIndicator: {
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    bottom: 4,
    right: 4,
    backgroundColor: colorPalette.white,
    width: 24,
    height: 24,
    borderRadius: 24,
  },
  userIcon: {
    borderRadius: 100,
    height: 109,
    width: 109,
    alignSelf: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 32,
    marginBottom: 16,
  },
  imageWrapper: {
    width: '100%',
    height: '100%',
    borderRadius: 100,
  },
  dateContainer: {
    paddingTop: 4,
  },
  ratingContainer: {
    marginTop: 20,
  },
  cardsOverlay: {
    alignContent: 'center',
    alignSelf: 'center',
    alignItems: 'center',
    width: width(90),
    marginTop: -height(6),
    marginBottom: totalSize(4),
  },
  cardsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  cardContainer: {
    flex: 1,
    height: '100%',
    shadowColor: '#050B5A',
    shadowOffset: {
      width: 0,
      height: 10,
    },
    shadowOpacity: 0.1,
    shadowRadius: 5,
    borderRadius: 10,
    elevation: 10,
    zIndex: 10,
    backgroundColor: colorPalette.white,
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  rightContainer: {
    marginLeft: totalSize(2),
  },
  cardContent: {
    width: '100%',
    height: '100%',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardIcon: {
    marginBottom: 14,
  },
  formContainer: {
    paddingHorizontal: totalSize(2.5),
  },
  infoContainer: {
    marginBottom: totalSize(2),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    // flex: 1,
    borderBottomWidth: 1,
    borderColor: colorPalette.light,
  },
  iconContainer: {
    width: width(15),
    marginBottom: totalSize(1.5),
  },
  formTitleContainer: {
    marginBottom: 20,
    flexDirection: 'row',
  },
  sectionContainer: {
    marginBottom: totalSize(1),
  },
  actionWrapper: {
    marginTop: 24,
    flexDirection: 'row',
  },
  buttonContainer: {
    flex: 1,
    maxWidth: width(45),
    alignSelf: 'center',
  },
  spinnerContainer: {
    width: '100%',
    justifyContent: 'flex-end',
  },
  overlayContainerMobile: {
    borderRadius: 20,
    paddingHorizontal: 32,
    paddingVertical: 24,
  },
  shareOverlayMobile: {
    borderRadius: 20,
    padding: 0,
  },
  nameContainer: {
    width: Dimensions.get('screen').width - 64,
    paddingHorizontal: 32,
  },
});
